import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUsers, FiCheckCircle, FiBarChart2, FiStar, FiSearch, FiPlus, FiEdit2, FiTrash2, FiX, FiShield, FiDollarSign, FiSettings, FiBook, FiCreditCard, FiTrendingUp, FiAward, FiBriefcase, FiClock, FiLogOut, FiUser, FiMail, FiHash, FiTarget, FiZap, FiSmartphone, FiUpload, FiSave, FiImage, FiCalendar, FiPhone, FiEye, FiRefreshCw } from 'react-icons/fi';
import PaymentManagement from './PaymentManagement';
import CaseStudyForm from '../components/CaseStudyForm';
import { useToast } from '../components/Toast';

const LAWYERS_API_URL = `${process.env.REACT_APP_API_URL}/api/lawyers`;
const CASE_STUDIES_API_URL = `${process.env.REACT_APP_API_URL}/api/case-studies`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

function AdminDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('lawyers');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approvalReason, setApprovalReason] = useState('');

  const [lawyers, setLawyers] = useState([]);
  const [pendingLawyers, setPendingLawyers] = useState([]);
  
  // Case Studies State
  const [caseStudies, setCaseStudies] = useState([]);
  const [showCaseStudyForm, setShowCaseStudyForm] = useState(false);
  const [editingCaseStudyId, setEditingCaseStudyId] = useState(null);

  // Bookings State
  const [allBookings, setAllBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [bookingSearch, setBookingSearch] = useState('');
  const [caseStudyForm, setCaseStudyForm] = useState({
    title: '',
    category: 'General',
    description: '',
    case_background: '',
    legal_issue: '',
    solution_provided: '',
    outcome: '',
    featured_image: '',
    tags: '',
    status: 'draft'
  });

  // Payment Settings State
  const [paymentConfig, setPaymentConfig] = useState({
    upi_id: '',
    upi_display_name: 'Bihar Lawyer Legal Services',
    qr_code_image: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    payment_instructions: '',
    platform_commission_rate: '12.5'
  });
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  // Fetch lawyers and case studies from backend
  useEffect(() => {
    fetchLawyers();
    fetchPendingLawyers();
    fetchCaseStudies();
    fetchPaymentSettings();
    fetchAllBookings();
  }, []);

  const fetchLawyers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${LAWYERS_API_URL}?approval=all`);
      if (response.data.success) {
        // Filter to show only approved lawyers
        const approved = response.data.data.filter(l => l.approval_status === 'approved');
        setLawyers(approved);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching lawyers:', err);
      setError('Failed to load lawyers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingLawyers = async () => {
    try {
      const response = await axios.get(`${LAWYERS_API_URL}?approval=all`);
      if (response.data.success) {
        // Filter to show only pending lawyers
        const pending = response.data.data.filter(l => l.approval_status === 'pending');
        setPendingLawyers(pending);
      }
    } catch (err) {
      console.error('Error fetching pending lawyers:', err);
    }
  };

  const fetchCaseStudies = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const userData = JSON.parse(localStorage.getItem('user_data'));
      const adminId = userData?.id;
      
      if (!adminId) return;
      
      const response = await axios.get(`${CASE_STUDIES_API_URL}?admin_id=${adminId}&status=all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setCaseStudies(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching case studies:', err);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/payments/settings?mode=admin`);
      if (response.data.success && Array.isArray(response.data.data)) {
        const configObj = {};
        response.data.data.forEach(item => {
          configObj[item.setting_key] = item.setting_value || '';
        });
        setPaymentConfig(prev => ({ ...prev, ...configObj }));
        if (configObj.qr_code_image) {
          setQrCodePreview(`${process.env.REACT_APP_API_URL}${configObj.qr_code_image}`);
        }
      }
    } catch (err) {
      console.error('Error fetching payment settings:', err);
    }
  };

  const fetchAllBookings = async () => {
    try {
      setBookingsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/bookings/admin`);
      if (response.data.success) {
        setAllBookings(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    try {
      setSavingPayment(true);
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      
      // Add settings as JSON
      const settings = { ...paymentConfig };
      delete settings.qr_code_image; // Handle file separately
      formData.append('settings', JSON.stringify(settings));
      
      // Add QR code file if selected
      if (qrCodeFile) {
        formData.append('qr_code_image', qrCodeFile);
      }
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/settings`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success('Payment settings saved successfully!');
        fetchPaymentSettings();
      }
    } catch (err) {
      console.error('Error saving payment settings:', err);
      toast.error('Failed to save payment settings');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleQrCodeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQrCodeFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setQrCodePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCaseStudy = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (editingCaseStudyId) {
        // Update existing
        await axios.put(`${CASE_STUDIES_API_URL}/manage.php?id=${editingCaseStudyId}`, caseStudyForm, { headers });
        toast.success('Case study updated successfully!');
      } else {
        // Create new
        await axios.post(CASE_STUDIES_API_URL, caseStudyForm, { headers });
        toast.success('Case study created successfully!');
      }
      
      setCaseStudyForm({
        title: '',
        category: 'General',
        description: '',
        case_background: '',
        legal_issue: '',
        solution_provided: '',
        outcome: '',
        featured_image: '',
        tags: '',
        status: 'draft'
      });
      setShowCaseStudyForm(false);
      setEditingCaseStudyId(null);
      fetchCaseStudies();
    } catch (err) {
      console.error('Error saving case study:', err);
      toast.error('Failed to save case study');
    }
  };

  const handleDeleteCaseStudy = async (id) => {
    if (window.confirm('Are you sure you want to delete this case study?')) {
      try {
        const token = localStorage.getItem('auth_token');
        await axios.delete(`${CASE_STUDIES_API_URL}/manage.php?id=${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Case study deleted successfully!');
        fetchCaseStudies();
      } catch (err) {
        console.error('Error deleting case study:', err);
        toast.error('Failed to delete case study');
      }
    }
  };

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    specialty: '',
    experience: '',
    rate: '',
    cases: '',
    successRate: '',
    license_number: '',
    bar_association: '',
    bio: '',
    education: '',
    languages: '',
    city: '',
    phone: '',
    availability: 'full-time'
  });

  const specialties = [
    'Criminal Defense',
    'Family Law',
    'Corporate Law',
    'Real Estate',
    'Immigration',
    'Intellectual Property',
    'Tax Law',
    'Personal Injury'
  ];

  const filteredLawyers = lawyers.filter(lawyer => {
    const matchesSearch = lawyer.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          lawyer.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpecialty = !filterSpecialty || (lawyer.specialty && lawyer.specialty.split(',').map(s => s.trim()).includes(filterSpecialty));
    return matchesSearch && matchesSpecialty;
  });

  const handleAddLawyer = () => {
    setSelectedLawyer(null);
    setFormData({
      full_name: '',
      email: '',
      specialty: '',
      experience: '',
      rate: '',
      cases: '',
      successRate: '',
      license_number: '',
      bar_association: '',
      bio: '',
      education: '',
      languages: '',
      city: '',
      phone: '',
      availability: 'full-time'
    });
    setShowAddModal(true);
  };

  const handleEditLawyer = (lawyer) => {
    setSelectedLawyer(lawyer);
    setFormData({
      full_name: lawyer.full_name,
      email: lawyer.email,
      specialty: lawyer.specialty,
      experience: lawyer.experience.toString(),
      rate: lawyer.rate.toString(),
      cases: lawyer.cases.toString(),
      successRate: lawyer.successRate.toString(),
      license_number: lawyer.license_number || '',
      bar_association: lawyer.bar_association || '',
      bio: lawyer.bio || '',
      education: lawyer.education || '',
      languages: lawyer.languages || '',
      city: lawyer.city || '',
      phone: lawyer.phone || '',
      availability: lawyer.availability || 'full-time'
    });
    setShowEditModal(true);
  };

  const handleSaveLawyer = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.specialty) {
      toast.warning('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (selectedLawyer) {
        // Edit existing lawyer
        const response = await axios.put(`${LAWYERS_API_URL}/edit.php?id=${selectedLawyer.id}`, {
          full_name: formData.full_name,
          email: formData.email,
          specialty: formData.specialty,
          experience: parseInt(formData.experience),
          rate: parseInt(formData.rate),
          cases: parseInt(formData.cases),
          successRate: parseInt(formData.successRate),
          license_number: formData.license_number,
          bar_association: formData.bar_association,
          bio: formData.bio,
          education: formData.education,
          languages: formData.languages,
          city: formData.city,
          phone: formData.phone,
          availability: formData.availability
        }, { headers });

        if (response.data.success) {
          await fetchLawyers();
          setShowEditModal(false);
          toast.success('Lawyer updated successfully!');
        }
      } else {
        // Add new lawyer
        const response = await axios.post(LAWYERS_API_URL, {
          full_name: formData.full_name,
          email: formData.email,
          specialty: formData.specialty,
          experience: parseInt(formData.experience),
          rate: parseInt(formData.rate),
          cases: parseInt(formData.cases),
          successRate: parseInt(formData.successRate),
          license_number: formData.license_number,
          bar_association: formData.bar_association,
          bio: formData.bio,
          education: formData.education,
          languages: formData.languages,
          city: formData.city,
          phone: formData.phone,
          availability: formData.availability
        }, { headers });

        if (response.data.success) {
          await fetchLawyers();
          setShowAddModal(false);
          toast.success('Lawyer added successfully!');
        }
      }
    } catch (err) {
      console.error('Error saving lawyer:', err);
      toast.error(err.response?.data?.message || 'Failed to save lawyer');
    }
  };

  const handleDeleteLawyer = async (id) => {
    if (window.confirm('Are you sure you want to delete this lawyer?')) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.delete(`${LAWYERS_API_URL}/edit.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.data.success) {
          await fetchLawyers();
          toast.success('Lawyer deleted successfully!');
        }
      } catch (err) {
        console.error('Error deleting lawyer:', err);
        toast.error('Failed to delete lawyer');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    // Status toggle can be implemented later if needed
    // For now, we'll update the verified status in the database
  };

  const handleApproveLawyer = async (lawyerId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(`${LAWYERS_API_URL}/edit.php?id=${lawyerId}`, {
        approval_status: 'approved'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        await fetchPendingLawyers();
        await fetchLawyers();
        toast.success('Lawyer approved successfully!');
        setSelectedLawyer(null);
      }
    } catch (err) {
      console.error('Error approving lawyer:', err);
      toast.error('Failed to approve lawyer');
    }
  };

  const handleRejectLawyer = async (lawyerId) => {
    if (!approvalReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(`${LAWYERS_API_URL}/edit.php?id=${lawyerId}`, {
        approval_status: 'rejected',
        rejection_reason: approvalReason
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        await fetchPendingLawyers();
        await fetchLawyers();
        toast.success('Lawyer rejected successfully!');
        setSelectedLawyer(null);
        setApprovalReason('');
      }
    } catch (err) {
      console.error('Error rejecting lawyer:', err);
      toast.error('Failed to reject lawyer');
    }
  };

  const stats = {
    totalLawyers: lawyers.length,
    activeLawyers: lawyers.filter(l => l.status === 'active').length,
    totalCases: lawyers.reduce((sum, l) => sum + (l.cases || 0), 0),
    avgSuccessRate: lawyers.length > 0 ? Math.round(lawyers.reduce((sum, l) => sum + (l.successRate || 0), 0) / lawyers.length) : 0
  };

  const tabs = [
    { key: 'lawyers', label: 'All Lawyers', icon: FiUsers },
    { key: 'approvals', label: 'Pending Approvals', icon: FiCheckCircle, badge: pendingLawyers.length },
    { key: 'bookings', label: 'Bookings', icon: FiCalendar, badge: allBookings.filter(b => b.status === 'scheduled').length },
    { key: 'case-studies', label: 'Case Studies', icon: FiBook },
    { key: 'payments', label: 'Payments', icon: FiCreditCard },
    { key: 'analytics', label: 'Analytics', icon: FiBarChart2 },
    { key: 'settings', label: 'Settings', icon: FiSettings },
  ];

  const statCards = [
    { label: 'Total Lawyers', value: stats.totalLawyers, icon: FiUsers, color: 'from-primary-500 to-primary-400', textColor: 'text-primary' },
    { label: 'Active Lawyers', value: stats.activeLawyers, icon: FiCheckCircle, color: 'from-teal-500 to-teal-400', textColor: 'text-teal-600' },
    { label: 'Total Cases', value: stats.totalCases, icon: FiBriefcase, color: 'from-accent to-yellow-400', textColor: 'text-accent' },
    { label: 'Avg Success Rate', value: `${stats.avgSuccessRate}%`, icon: FiStar, color: 'from-purple-600 to-purple-400', textColor: 'text-purple-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-primary-500 via-primary-400 to-[#2a4a6f] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <FiShield className="w-5 h-5" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold">Super Admin Dashboard</h1>
              </div>
              <p className="text-white/70 text-sm sm:text-base">Manage attorneys, view analytics, and control platform settings</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl border border-white/20 text-sm font-medium transition-all duration-200"
            >
              <FiLogOut className="w-4 h-4" />
              Logout
            </button>
          </motion.div>
        </div>
      </div>

      {/* STATS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {statCards.map((stat, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              className="bg-white rounded-2xl shadow-card p-5 flex items-center gap-4 hover:shadow-card-hover transition-shadow duration-300"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center flex-shrink-0`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* TABS */}
        <div className="bg-white rounded-2xl shadow-card mb-6 p-1.5 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-primary'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-red-100 text-red-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* LAWYERS TAB */}
        {activeTab === 'lawyers' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {/* TOOLBAR */}
            <div className="bg-white rounded-2xl shadow-card p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select
                    className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white"
                    value={filterSpecialty}
                    onChange={(e) => setFilterSpecialty(e.target.value)}
                  >
                    <option value="">All Specialties</option>
                    {specialties.map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleAddLawyer}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-400 text-white rounded-xl text-sm font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  <FiPlus className="w-4 h-4" />
                  Add New Lawyer
                </button>
              </div>
            </div>

            {/* LAWYERS TABLE */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-500">Loading lawyers...</span>
                </div>
              ) : error ? (
                <div className="text-center py-20 text-red-500">{error}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name & Contact</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Specialty</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Experience</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hourly Rate</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cases</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Success Rate</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredLawyers.length > 0 ? (
                        filteredLawyers.map(lawyer => (
                          <tr key={lawyer.id} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                {lawyer.profile_image ? (
                                  <img 
                                    src={`${process.env.REACT_APP_API_URL}${lawyer.profile_image}`}
                                    alt={lawyer.full_name}
                                    className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                                    {lawyer.avatar || lawyer.full_name?.charAt(0)}
                                  </div>
                                )}
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">{lawyer.full_name}</p>
                                  <p className="text-xs text-gray-500">{lawyer.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="inline-flex px-2.5 py-1 bg-primary/5 text-primary text-xs font-medium rounded-lg">{lawyer.specialty}</span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">{lawyer.experience} years</td>
                            <td className="px-4 py-4 text-sm font-semibold text-gray-900">₹{lawyer.rate}/hr</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{lawyer.cases}</td>
                            <td className="px-4 py-4">
                              <span className="inline-flex px-2.5 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-lg">{lawyer.successRate}%</span>
                            </td>
                            <td className="px-4 py-4">
                              <button
                                onClick={() => handleToggleStatus(lawyer.id)}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                  lawyer.verified
                                    ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                                }`}
                              >
                                {lawyer.verified ? <><FiCheckCircle className="w-3 h-3" /> Active</> : <><FiX className="w-3 h-3" /> Inactive</>}
                              </button>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleEditLawyer(lawyer)}
                                  title="Edit"
                                  className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                >
                                  <FiEdit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteLawyer(lawyer.id)}
                                  title="Delete"
                                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-16 text-gray-400">
                            <FiUsers className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p className="font-medium">No lawyers found matching your criteria</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* APPROVALS TAB */}
        {activeTab === 'approvals' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FiClock className="w-5 h-5 text-accent" />
                Pending Lawyer Approvals
              </h2>
            </div>
              
            {pendingLawyers.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-card p-16 text-center">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-lg font-semibold text-gray-900">All caught up!</p>
                <p className="text-gray-500 mt-1">No pending lawyer approvals at this time.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pendingLawyers.map(lawyer => (
                  <motion.div
                    key={lawyer.id}
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    className="bg-white rounded-2xl shadow-card overflow-hidden hover:shadow-card-hover transition-shadow duration-300"
                  >
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-5 border-b border-amber-100">
                      <div className="flex items-center gap-4">
                        {lawyer.profile_image ? (
                          <img 
                            src={`${process.env.REACT_APP_API_URL}${lawyer.profile_image}`}
                            alt={lawyer.full_name}
                            className="w-14 h-14 rounded-xl object-cover ring-2 ring-amber-200"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-lg">
                            {lawyer.avatar || lawyer.full_name?.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">{lawyer.full_name}</h3>
                          <span className="inline-flex px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full mt-1">PENDING</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</label>
                        <p className="text-sm text-gray-700 mt-0.5">{lawyer.email}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Specialization</label>
                          <p className="text-sm text-gray-700 mt-0.5">{lawyer.specialty}</p>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Experience</label>
                          <p className="text-sm text-gray-700 mt-0.5">{lawyer.experience} years</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Hourly Rate</label>
                          <p className="text-sm text-gray-700 mt-0.5">₹{lawyer.rate}/hr</p>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">License Number</label>
                          <p className="text-sm text-gray-700 mt-0.5">{lawyer.license_number}</p>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bar Association</label>
                        <p className="text-sm text-gray-700 mt-0.5">{lawyer.bar_association}</p>
                      </div>

                      {selectedLawyer?.id === lawyer.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="pt-3 border-t border-gray-100 space-y-3"
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason (if rejecting)</label>
                            <textarea
                              value={approvalReason}
                              onChange={(e) => setApprovalReason(e.target.value)}
                              placeholder="Explain why you're rejecting this lawyer..."
                              rows="3"
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                            />
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApproveLawyer(lawyer.id)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold transition-colors"
                            >
                              <FiCheckCircle className="w-4 h-4" /> Approve
                            </button>
                            <button
                              onClick={() => handleRejectLawyer(lawyer.id)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors"
                            >
                              <FiX className="w-4 h-4" /> Reject
                            </button>
                            <button
                              onClick={() => {
                                setSelectedLawyer(null);
                                setApprovalReason('');
                              }}
                              className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {selectedLawyer?.id !== lawyer.id && (
                        <button
                          onClick={() => setSelectedLawyer(lawyer)}
                          className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary/5 hover:bg-primary/10 text-primary rounded-xl text-sm font-semibold transition-colors"
                        >
                          Review Application
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* CASE STUDIES TAB */}
        {activeTab === 'case-studies' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FiBook className="w-5 h-5 text-accent" />
                Case Studies
              </h2>
              <button 
                onClick={() => {
                  setShowCaseStudyForm(!showCaseStudyForm);
                  setEditingCaseStudyId(null);
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors shadow-md ${
                  showCaseStudyForm
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-primary text-white hover:bg-primary-400'
                }`}
              >
                {showCaseStudyForm ? <><FiX className="w-4 h-4" /> Cancel</> : <><FiPlus className="w-4 h-4" /> Add New Case Study</>}
              </button>
            </div>

            {showCaseStudyForm && (
              <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                <CaseStudyForm 
                  onSave={() => {
                    setShowCaseStudyForm(false);
                    setEditingCaseStudyId(null);
                    fetchCaseStudies();
                  }}
                  onCancel={() => {
                    setShowCaseStudyForm(false);
                    setEditingCaseStudyId(null);
                  }}
                  initialData={editingCaseStudyId ? caseStudies.find(cs => cs.id === editingCaseStudyId) : null}
                />
              </div>
            )}

            <div>
              <p className="text-sm text-gray-500 mb-4">{caseStudies.length} case {caseStudies.length === 1 ? 'study' : 'studies'} total</p>
              {caseStudies.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-card p-16 text-center">
                  <FiBook className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No case studies yet.</p>
                  <p className="text-sm text-gray-400 mt-1">Click "Add New Case Study" to create one.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {caseStudies.map(study => (
                    <motion.div
                      key={study.id}
                      variants={fadeUp}
                      initial="hidden"
                      animate="visible"
                      className="bg-white rounded-2xl shadow-card overflow-hidden hover:shadow-card-hover transition-shadow duration-300 group"
                    >
                      {study.featured_image && (
                        <div className="h-40 overflow-hidden">
                          <img 
                            src={`${process.env.REACT_APP_API_URL}${study.featured_image}`}
                            alt={study.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <h4 className="font-bold text-gray-900 mb-1 line-clamp-2">{study.title}</h4>
                        <span className="inline-flex px-2.5 py-0.5 bg-primary/5 text-primary text-xs font-medium rounded-lg mb-2">{study.category}</span>
                        <p className="text-sm text-gray-500 line-clamp-2 mb-4">{study.description?.substring(0, 80)}...</p>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ${
                            study.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {study.status.toUpperCase()}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingCaseStudyId(study.id);
                                setShowCaseStudyForm(true);
                              }}
                              title="Edit"
                              className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCaseStudy(study.id)}
                              title="Delete"
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <PaymentManagement />
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {[
              { title: 'Top Performing Lawyers', icon: FiTrendingUp, color: 'text-green-500', data: [...lawyers].sort((a, b) => b.successRate - a.successRate).slice(0, 5), valueKey: 'successRate', valueSuffix: '% success' },
              { title: 'Highest Rated Lawyers', icon: FiDollarSign, color: 'text-accent', data: [...lawyers].sort((a, b) => b.rate - a.rate).slice(0, 5), valueKey: 'rate', valuePrefix: '₹', valueSuffix: '/hour' },
              { title: 'Most Experienced', icon: FiAward, color: 'text-purple-500', data: [...lawyers].sort((a, b) => b.experience - a.experience).slice(0, 5), valueKey: 'experience', valueSuffix: ' years' },
              { title: 'Most Cases Won', icon: FiTarget, color: 'text-primary', data: [...lawyers].sort((a, b) => b.cases - a.cases).slice(0, 5), valueKey: 'cases', valueSuffix: ' cases' },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-card p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center ${card.color}`}>
                    <card.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-gray-900">{card.title}</h3>
                </div>
                <div className="space-y-3">
                  {card.data.map((lawyer, idx) => (
                    <div key={lawyer.id} className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <span className="w-7 h-7 bg-primary/5 text-primary rounded-lg flex items-center justify-center text-xs font-bold">#{idx + 1}</span>
                      <span className="flex-1 text-sm font-medium text-gray-700 truncate">{lawyer.full_name}</span>
                      <span className="text-sm font-semibold text-gray-900">{card.valuePrefix || ''}{lawyer[card.valueKey]}{card.valueSuffix}</span>
                    </div>
                  ))}
                  {card.data.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No data available</p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* BOOKINGS TAB */}
        {activeTab === 'bookings' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {/* Toolbar */}
            <div className="bg-white rounded-2xl shadow-card p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      placeholder="Search by client or lawyer name..."
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                    />
                  </div>
                  <select
                    value={bookingFilter}
                    onChange={(e) => setBookingFilter(e.target.value)}
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <button
                  onClick={fetchAllBookings}
                  className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-400 transition-colors"
                >
                  <FiRefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>
            </div>

            {/* Booking Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total', value: allBookings.length, color: 'bg-primary-500', icon: FiCalendar },
                { label: 'Scheduled', value: allBookings.filter(b => b.status === 'scheduled').length, color: 'bg-yellow-500', icon: FiClock },
                { label: 'Completed', value: allBookings.filter(b => b.status === 'completed').length, color: 'bg-teal-500', icon: FiCheckCircle },
                { label: 'Cancelled', value: allBookings.filter(b => b.status === 'cancelled').length, color: 'bg-red-500', icon: FiX },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-card p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Bookings Table */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              {bookingsLoading ? (
                <div className="p-10 text-center">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Loading bookings...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left">ID</th>
                        <th className="px-4 py-3 text-left">Client</th>
                        <th className="px-4 py-3 text-left">Lawyer</th>
                        <th className="px-4 py-3 text-left">Service</th>
                        <th className="px-4 py-3 text-left">Date & Time</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Contact</th>
                        <th className="px-4 py-3 text-left">Booked On</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {allBookings
                        .filter(b => {
                          if (bookingFilter !== 'all' && b.status !== bookingFilter) return false;
                          if (bookingSearch) {
                            const q = bookingSearch.toLowerCase();
                            return (b.client_name || '').toLowerCase().includes(q) ||
                                   (b.lawyer_name || '').toLowerCase().includes(q) ||
                                   (b.client_email || '').toLowerCase().includes(q);
                          }
                          return true;
                        })
                        .map((booking) => (
                        <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-gray-500">#{booking.id}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                                {(booking.client_name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{booking.client_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-400">
                                  {booking.is_guest ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded text-[10px] font-medium">Guest</span>
                                  ) : (
                                    <span className="text-gray-400">Registered</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{booking.lawyer_name || 'N/A'}</p>
                            <p className="text-xs text-gray-400">{booking.lawyer_specialty || ''}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                              booking.service_type === 'video' ? 'bg-blue-50 text-blue-600' :
                              booking.service_type === 'phone' ? 'bg-green-50 text-green-600' :
                              'bg-purple-50 text-purple-600'
                            }`}>
                              {booking.service_type === 'video' ? '📹 Video' : booking.service_type === 'phone' ? '📞 Phone' : '🏢 In-Person'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-gray-900">{booking.consultation_date ? new Date(booking.consultation_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</p>
                            <p className="text-xs text-gray-400">{booking.consultation_date ? new Date(booking.consultation_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''} · {booking.duration_minutes}min</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              booking.status === 'scheduled' ? 'bg-yellow-50 text-yellow-700' :
                              booking.status === 'confirmed' ? 'bg-blue-50 text-blue-700' :
                              booking.status === 'completed' ? 'bg-teal-50 text-teal-700' :
                              booking.status === 'cancelled' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-600'
                            }`}>
                              {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {booking.client_email && (
                                <a href={`mailto:${booking.client_email}`} className="flex items-center gap-1 text-xs text-primary-600 hover:underline">
                                  <FiMail className="w-3 h-3" /> {booking.client_email}
                                </a>
                              )}
                              {booking.client_phone && (
                                <a href={`tel:${booking.client_phone}`} className="flex items-center gap-1 text-xs text-teal-600 hover:underline">
                                  <FiPhone className="w-3 h-3" /> {booking.client_phone}
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400">
                            {booking.created_at ? new Date(booking.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                      {allBookings.filter(b => {
                        if (bookingFilter !== 'all' && b.status !== bookingFilter) return false;
                        if (bookingSearch) {
                          const q = bookingSearch.toLowerCase();
                          return (b.client_name || '').toLowerCase().includes(q) || (b.lawyer_name || '').toLowerCase().includes(q);
                        }
                        return true;
                      }).length === 0 && (
                        <tr><td colSpan="8" className="px-4 py-10 text-center text-gray-400">
                          <FiCalendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p>No bookings found</p>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {/* Platform Settings Card */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FiSettings className="w-5 h-5 text-accent" />
                  Platform Settings
                </h3>
              </div>

              <div className="divide-y divide-gray-100">
                {/* Lawyer Management */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Lawyer Management</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                      <label className="text-sm font-medium text-gray-700">Minimum Hourly Rate</label>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-1.5 rounded-lg">₹1,500/hour</span>
                        <button className="px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors">Edit</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <label className="text-sm font-medium text-gray-700">Maximum Hourly Rate</label>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-1.5 rounded-lg">₹10,000/hour</span>
                        <button className="px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors">Edit</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Verification Requirements */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Verification Requirements</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                      <label className="text-sm font-medium text-gray-700">Require Bar License Verification</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <label className="text-sm font-medium text-gray-700">Require Background Check</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Commission Settings */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Commission Settings</h4>
                  <div className="flex items-center justify-between py-2">
                    <label className="text-sm font-medium text-gray-700">Platform Commission Rate</label>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-1.5 rounded-lg">{paymentConfig.platform_commission_rate || '12.5'}%</span>
                      <button className="px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors">Edit</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Configuration Card */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FiCreditCard className="w-5 h-5 text-primary" />
                    Payment Configuration
                  </h3>
                  <span className="px-3 py-1 bg-primary-50 text-primary text-xs font-semibold rounded-full">Used across website</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Configure payment methods that will be shown to clients during booking</p>
              </div>

              <div className="divide-y divide-gray-100">
                {/* UPI Settings */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FiSmartphone className="w-4 h-4" /> BHIM UPI Settings
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">UPI ID</label>
                      <input
                        type="text"
                        value={paymentConfig.upi_id}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upi_id: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="e.g. business@upi or 9876543210@ybl"
                      />
                      <p className="text-xs text-gray-400 mt-1">Clients will use this UPI ID to make payments</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Name / Payee Name</label>
                      <input
                        type="text"
                        value={paymentConfig.upi_display_name}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upi_display_name: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="e.g. Bihar Lawyer Legal Services"
                      />
                    </div>
                  </div>
                </div>

                {/* QR Code Settings */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FiImage className="w-4 h-4" /> QR Code
                  </h4>
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload QR Code Image</label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleQrCodeChange}
                          className="hidden"
                          id="qr-code-upload"
                        />
                        <label
                          htmlFor="qr-code-upload"
                          className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary hover:bg-primary-50/30 transition-all text-sm text-gray-500"
                        >
                          <FiUpload className="w-4 h-4" />
                          {qrCodeFile ? qrCodeFile.name : 'Click to upload QR code image'}
                        </label>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, or WEBP. Max 5MB. Generate from any UPI app.</p>
                    </div>
                    {qrCodePreview && (
                      <div className="flex-shrink-0">
                        <p className="text-xs font-medium text-gray-500 mb-2">Preview:</p>
                        <div className="w-32 h-32 border-2 border-gray-200 rounded-xl overflow-hidden bg-white p-1">
                          <img src={qrCodePreview} alt="QR Code Preview" className="w-full h-full object-contain" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Transfer Settings */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    🏦 Bank Transfer Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Bank Name</label>
                      <input
                        type="text"
                        value={paymentConfig.bank_name}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, bank_name: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="e.g. State Bank of India"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Account Holder Name</label>
                      <input
                        type="text"
                        value={paymentConfig.bank_account_name}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, bank_account_name: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="e.g. Bihar Lawyer Legal Services Pvt Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Account Number</label>
                      <input
                        type="text"
                        value={paymentConfig.bank_account_number}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, bank_account_number: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono"
                        placeholder="e.g. 1234567890123456"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">IFSC Code</label>
                      <input
                        type="text"
                        value={paymentConfig.bank_ifsc_code}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, bank_ifsc_code: e.target.value.toUpperCase() }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono uppercase"
                        placeholder="e.g. SBIN0001234"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Instructions */}
                <div className="p-6">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Payment Instructions</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Instructions for Clients</label>
                    <textarea
                      value={paymentConfig.payment_instructions}
                      onChange={(e) => setPaymentConfig(prev => ({ ...prev, payment_instructions: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                      placeholder="Instructions shown to clients during payment..."
                    />
                    <p className="text-xs text-gray-400 mt-1">This message will be displayed on the booking payment page</p>
                  </div>
                </div>

                {/* Save Button */}
                <div className="p-6 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      <FiShield className="w-4 h-4 inline mr-1 text-primary" />
                      These settings apply to all bookings across the platform
                    </p>
                    <button
                      onClick={handleSavePaymentSettings}
                      disabled={savingPayment}
                      className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary-400 transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingPayment ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
                      ) : (
                        <><FiSave className="w-4 h-4" /> Save Payment Settings</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* ADD/EDIT LAWYER MODAL */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowAddModal(false);
              setShowEditModal(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  {selectedLawyer ? <><FiEdit2 className="w-5 h-5 text-accent" /> Edit Lawyer</> : <><FiPlus className="w-5 h-5 text-accent" /> Add New Lawyer</>}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <FiX className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSaveLawyer} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* PERSONAL INFO */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2"><FiUser className="w-4 h-4" /> Personal Information</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        placeholder="Enter full name"
                        required
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter email address"
                        required
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                  </div>

                  {/* PROFESSIONAL INFO */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2"><FiBriefcase className="w-4 h-4" /> Professional Information</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specialties * <span className="text-xs text-gray-400 font-normal">(select multiple)</span></label>
                      <div className="grid grid-cols-2 gap-2 mt-1 max-h-48 overflow-y-auto p-3 border border-gray-200 rounded-xl bg-gray-50">
                        {specialties.map(spec => {
                          const selected = formData.specialty ? formData.specialty.split(',').map(s => s.trim()).includes(spec) : false;
                          return (
                            <label
                              key={spec}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                                selected ? 'bg-primary/10 text-primary font-medium border border-primary/30' : 'bg-white text-gray-600 border border-gray-200 hover:border-primary/20'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => {
                                  const current = formData.specialty ? formData.specialty.split(',').map(s => s.trim()).filter(Boolean) : [];
                                  const updated = selected
                                    ? current.filter(s => s !== spec)
                                    : [...current, spec];
                                  setFormData({ ...formData, specialty: updated.join(', ') });
                                }}
                                className="accent-primary w-4 h-4"
                              />
                              {spec}
                            </label>
                          );
                        })}
                      </div>
                      {!formData.specialty && <p className="text-xs text-red-400 mt-1">Please select at least one specialty</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                      <input
                        type="number"
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        placeholder="e.g., 15"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (₹)</label>
                      <input
                        type="number"
                        value={formData.rate}
                        onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                        placeholder="e.g., 200"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                  </div>

                  {/* PERFORMANCE METRICS */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2"><FiBarChart2 className="w-4 h-4" /> Performance Metrics</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cases Won</label>
                      <input
                        type="number"
                        value={formData.cases}
                        onChange={(e) => setFormData({ ...formData, cases: e.target.value })}
                        placeholder="e.g., 320"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Success Rate (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.successRate}
                        onChange={(e) => setFormData({ ...formData, successRate: e.target.value })}
                        placeholder="e.g., 92"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                  </div>

                  {/* CREDENTIALS */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2"><FiHash className="w-4 h-4" /> Bar & License Info</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                      <input
                        type="text"
                        value={formData.license_number}
                        onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                        placeholder="e.g., NYB123456"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bar Association</label>
                      <input
                        type="text"
                        value={formData.bar_association}
                        onChange={(e) => setFormData({ ...formData, bar_association: e.target.value })}
                        placeholder="e.g., New York State Bar"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* ADDITIONAL DETAILS - full width row */}
                <div className="mt-6 space-y-4">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2"><FiUser className="w-4 h-4" /> Additional Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City / Location</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="e.g., Patna, Bihar"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="text"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="e.g., +91 9876543210"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                      <select
                        value={formData.availability}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white"
                      >
                        <option value="full-time">Full-time</option>
                        <option value="part-time">Part-time</option>
                        <option value="limited">Limited</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Languages</label>
                    <input
                      type="text"
                      value={formData.languages}
                      onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                      placeholder="e.g., English, Hindi, Bhojpuri"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Professional biography..."
                      rows="3"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Education & Qualifications</label>
                    <textarea
                      value={formData.education}
                      onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                      placeholder="e.g., BA LLB from Patna University&#10;LLM from NLU..."
                      rows="3"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                    }}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-400 rounded-xl transition-colors shadow-md hover:shadow-lg"
                  >
                    {selectedLawyer ? 'Update Lawyer' : 'Add Lawyer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminDashboard;
